import { EXPERIMENT_VARIANT_STORAGE_KEY } from 'config/storage';
import { rumService } from 'services/rum';

const KNOWN_PREFIXES = ['partners.acquisition.', 'partners.self_signup.'];
const TOKEN_SEPARATOR = ';';
const KEY_VALUE_SEPARATOR = '=';

type DecisionSource = 'growthbook' | 'unleash';

const sourceByKey = new Map<string, DecisionSource>();

function normalizeDelimiters(value: string) {
  return value
    .replaceAll(/[^a-z0-9]+/g, '_')
    .split('_')
    .filter(Boolean)
    .join('_');
}

function normalizeKey(key: string) {
  const keyWithoutPrefix = KNOWN_PREFIXES.reduce((current, prefix) => {
    return current.startsWith(prefix) ? current.slice(prefix.length) : current;
  }, key);

  return normalizeDelimiters(keyWithoutPrefix.toLowerCase());
}

function normalizeValue(value: unknown) {
  if (typeof value === 'boolean') {
    return value ? 'on' : 'off';
  }

  return normalizeDelimiters(String(value).toLowerCase());
}

function buildToken(key: string, value: unknown) {
  return `${normalizeKey(key)}${KEY_VALUE_SEPARATOR}${normalizeValue(value)}`;
}

function parseToken(token: string) {
  const separatorIndex = token.indexOf(KEY_VALUE_SEPARATOR);

  if (separatorIndex <= 0 || separatorIndex >= token.length - 1) {
    return null;
  }

  return {
    key: token.slice(0, separatorIndex),
    token,
  };
}

function parseRawString(raw?: string) {
  const tokens = (raw ?? '')
    .split(TOKEN_SEPARATOR)
    .map(item => item.trim())
    .filter(Boolean);

  const malformedTokens: string[] = [];
  const validTokenMap = new Map<string, string>();

  tokens.forEach(token => {
    const parsedToken = parseToken(token);

    if (!parsedToken) {
      malformedTokens.push(token);
      return;
    }

    validTokenMap.set(parsedToken.key, parsedToken.token);
  });

  return { validTokenMap, malformedTokens };
}

function serializeRawString({
  validTokenMap,
  malformedTokens,
}: {
  validTokenMap: Map<string, string>;
  malformedTokens: string[];
}) {
  const sortedValidTokens = [...validTokenMap.entries()]
    .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
    .map(([, token]) => token);

  return [...sortedValidTokens, ...malformedTokens].join(TOKEN_SEPARATOR);
}

function getStoredExperimentVariant() {
  const value = sessionStorage.getItem(EXPERIMENT_VARIANT_STORAGE_KEY)?.trim();
  return value?.length ? value : undefined;
}

function saveStoredExperimentVariant(value: string) {
  sessionStorage.setItem(EXPERIMENT_VARIANT_STORAGE_KEY, value);
}

function clearStoredExperimentVariant() {
  sourceByKey.clear();
  sessionStorage.removeItem(EXPERIMENT_VARIANT_STORAGE_KEY);
}

function mergeRecoveredExperimentVariant(value?: string) {
  const currentValue = getStoredExperimentVariant();

  if (!value || value === currentValue) {
    return currentValue;
  }

  const {
    validTokenMap: recoveredValidTokens,
    malformedTokens: recoveredMalformed,
  } = parseRawString(value);
  const {
    validTokenMap: currentValidTokens,
    malformedTokens: currentMalformed,
  } = parseRawString(currentValue);

  const mergedValidTokens = new Map([
    ...recoveredValidTokens.entries(),
    ...currentValidTokens.entries(),
  ]);
  const mergedMalformedTokens = [
    ...recoveredMalformed,
    ...currentMalformed.filter(token => !recoveredMalformed.includes(token)),
  ];

  const mergedValue = serializeRawString({
    validTokenMap: mergedValidTokens,
    malformedTokens: mergedMalformedTokens,
  });

  saveStoredExperimentVariant(mergedValue);

  return mergedValue;
}

function resolveExperimentVariantForPayload(
  currentValue?: string,
): string | undefined {
  return getStoredExperimentVariant() ?? currentValue;
}

function upsertExperimentVariantDecision({
  key,
  value,
  source,
}: {
  key: string;
  value: unknown;
  source: DecisionSource;
}) {
  const normalizedKey = normalizeKey(key);
  const nextToken = buildToken(key, value);
  const currentValue = getStoredExperimentVariant();
  const { validTokenMap, malformedTokens } = parseRawString(currentValue);

  const currentSource = sourceByKey.get(normalizedKey);
  const isCollision = currentSource && currentSource !== source;

  if (isCollision && source === 'unleash' && currentSource === 'growthbook') {
    rumService.addWarning(
      '[experimentVariant] key collision: growthbook wins',
      {
        key: normalizedKey,
        currentSource,
        incomingSource: source,
      },
    );

    return currentValue;
  }

  if (isCollision && source === 'growthbook' && currentSource === 'unleash') {
    rumService.addWarning(
      '[experimentVariant] key collision: growthbook overrides unleash',
      {
        key: normalizedKey,
        currentSource,
        incomingSource: source,
      },
    );
  }

  validTokenMap.set(normalizedKey, nextToken);
  sourceByKey.set(normalizedKey, source);

  const nextValue = serializeRawString({ validTokenMap, malformedTokens });
  saveStoredExperimentVariant(nextValue);

  return nextValue;
}

export const experimentVariantManager = {
  get: getStoredExperimentVariant,
  clear: clearStoredExperimentVariant,
  mergeRecovered: mergeRecoveredExperimentVariant,
  resolveForPayload: resolveExperimentVariantForPayload,
  upsertDecision: upsertExperimentVariantDecision,
};
